import React from 'react'
import { Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import {
  BAD,
  Callout,
  CountTable,
  InPairs,
  DataTable,
  Footer,
  GOOD,
  INK,
  LINE,
  MUTED,
  NAVY,
  PAGE_STYLE,
  Prose,
  ReportPage,
  SectionHeading,
  SectionTitle,
  StatRow,
  TEAL,
  WARN,
  clean,
  fmtDate,
  fmtDateTime,
  fmtMinutes,
  fmtPct,
  topEntries,
} from '../../../../../../components/PDF/pdfKit'
import { keyFindings } from '../findings'

const eventDates = (event) => {
  const start = moment(event.startDate)
  const end = event.endDate ? moment(event.endDate) : null
  if (!end || start.isSame(end, 'day')) {
    return `${start.format('dddd D MMMM YYYY')}${end ? `, ${start.format('HH:mm')} - ${end.format('HH:mm')}` : ''}`
  }
  return `${start.format('D MMMM YYYY')} - ${end.format('D MMMM YYYY')}`
}

const CoverField = ({ label, value }) =>
  value ? (
    <View style={{ flexDirection: 'row', paddingVertical: 3, borderBottomWidth: 0.5, borderBottomColor: LINE }}>
      <Text style={{ width: 110, fontSize: 7, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Text>
      <Text style={{ flex: 1, fontSize: 8, color: INK }}>{clean(value)}</Text>
    </View>
  ) : null

export const CoverPage = ({ data, chrome, contents, include }) => {
  const { event } = data
  const showHospital = include.part('clinical', 'outcomes') || include.part('nhsImpact', 'headline')
  return (
    <Page size="A4" style={{ ...PAGE_STYLE, paddingTop: 36 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        {chrome.logo ? (
          <Image src={chrome.logo} style={{ height: 52, maxWidth: 180, objectFit: 'contain' }} />
        ) : (
          <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY }}>{clean(chrome.orgName)}</Text>
        )}
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 7, color: MUTED }}>Generated {moment(chrome.generatedAt).format('dddd D MMMM YYYY, HH:mm')}</Text>
          {chrome.generatedBy ? <Text style={{ fontSize: 7, color: MUTED }}>by {clean(chrome.generatedBy)}</Text> : null}
        </View>
      </View>

      <View style={{ marginTop: 40, padding: 14, borderRadius: 6, backgroundColor: NAVY }}>
        <Text style={{ fontSize: 8, color: '#9FD6DA', letterSpacing: 1.4, textTransform: 'uppercase' }}>
          Post Event Report
        </Text>
        <Text style={{ fontSize: 26, color: '#FFFFFF', fontFamily: 'Helvetica-Bold', marginTop: 6, lineHeight: 1.15 }}>
          {clean(event.title)}
        </Text>
        <Text style={{ fontSize: 10, color: '#D7EEF0', marginTop: 6 }}>{clean(eventDates(event))}</Text>
        <View style={{ marginTop: 12, padding: 10, borderRadius: 5, backgroundColor: '#FFFFFF' }}>
          <Text style={{ fontSize: 7.5, color: NAVY, lineHeight: 1.45 }}>
            {clean(
              `A summary of the medical, first aid and welfare provision delivered by ${chrome.orgName} at this event: the demand we saw, how it was managed and what we learned. It is written for the event organiser and the Safety Advisory Group. Patient information is reported as totals only; no individual can be identified from this report.`,
            )}
          </Text>
        </View>
      </View>

      <View style={{ marginTop: 22 }}>
        <CoverField label="Prepared for" value={event.client} />
        <CoverField label="Prepared by" value={chrome.orgName} />
        <CoverField label="Venue" value={event.address || event.location} />
        <CoverField label="Event type" value={event.eventType} />
        <CoverField label="Expected attendance" value={event.expectedAttendance} />
        <CoverField label="Report version" value={`Version ${event.reportVersion} - ${fmtDate(chrome.generatedAt)}`} />
        <CoverField label="Classification" value="Confidential - for the organiser and Safety Advisory Group" />
      </View>

      <Text style={{ fontSize: 10.5, color: NAVY, fontFamily: 'Helvetica-Bold', marginTop: 22, marginBottom: 6 }}>
        At a glance
      </Text>
      <StatRow
        items={[
          { label: 'Patient presentations', value: data.overall.clinical.total },
          ...(showHospital ? [{ label: 'Went to hospital', value: data.overall.clinical.conveyed }] : []),
          ...(include.part('controlRoom', 'headline') ? [{ label: 'Control room incidents', value: data.controlRoom.summary.total }] : []),
          ...(include.part('overview', 'staff') ? [{ label: 'Staff deployed', value: data.staffing.overall.people }] : []),
          { label: 'Operational days', value: data.days.length, color: TEAL },
        ]}
      />

      <Text style={{ fontSize: 10.5, color: NAVY, fontFamily: 'Helvetica-Bold', marginTop: 16, marginBottom: 6 }}>
        In this report
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {contents.map((item) => (
          <View key={item.number} style={{ width: '50%', flexDirection: 'row', paddingVertical: 3, paddingRight: 10 }}>
            <Text style={{ width: 18, fontSize: 8, fontFamily: 'Helvetica-Bold', color: TEAL }}>{item.number}</Text>
            <Text style={{ fontSize: 8, color: INK }}>{clean(item.title)}</Text>
          </View>
        ))}
      </View>

      <Footer {...chrome} />
    </Page>
  )
}

const planned = (min, max, planning) => {
  if (planning !== undefined && planning !== null) {
    return min !== undefined && max !== undefined && min !== null && max !== null
      ? `${planning} (range ${min}-${max})`
      : String(planning)
  }
  if (min !== undefined && max !== undefined && min !== null && max !== null) return `${min}-${max}`
  return null
}

const verdict = (actual, min, max) => {
  if (actual === null || actual === undefined || min === null || min === undefined || max === null || max === undefined) return ''
  if (actual < min) return 'Below forecast'
  if (actual > max) return 'Above forecast'
  return 'Within forecast'
}

export const ExecutiveSummary = ({ number, data, chrome, include, pea }) => {
  const c = data.overall.clinical
  const room = data.controlRoom?.summary
  const fb = data.feedback
  const dp = data.event.demandPrediction || {}
  const discharged = (c.outcomes['Discharged on site'] || 0) + (c.outcomes['Advice only'] || 0)

  // A figure the planner left out of its own section stays out of here too
  const part = include.part
  const showHospital = part('clinical', 'outcomes') || part('nhsImpact', 'headline')
  const first = [
    { label: 'Patient presentations', value: c.total, sub: c.occurred?.Yes ? `${c.occurred.Yes} occurred at the event` : null },
  ]
  if (showHospital) {
    first.push({ label: 'Went to hospital', value: c.conveyed, sub: c.conveyanceRate !== null ? `${c.conveyanceRate}% of recorded outcomes` : null, color: c.conveyed ? WARN : GOOD })
    first.push({ label: 'Discharged on site', value: discharged, sub: c.total ? `${Math.round((discharged / c.total) * 100)}% of presentations` : null, color: GOOD })
  }
  if (room && part('controlRoom', 'headline')) {
    first.push({ label: 'CAD incidents', value: room.total, sub: room.cancelled ? `${room.cancelled} cancelled` : null })
    first.push({ label: 'Median response', value: fmtMinutes(room.intervals.callToScene.median), sub: 'call to first on scene', color: TEAL })
  }

  const second = []
  if (include.healthSafety) {
    second.push({ label: 'H&S concerns', value: data.overall.healthSafety, color: data.overall.healthSafety ? WARN : GOOD })
  }
  second.push({ label: 'Safeguarding', value: c.safeguarding, sub: c.safeguardingReferrals ? `${c.safeguardingReferrals} referred` : null })
  if (part('patientFeedback', 'counts')) {
    second.push({ label: 'Compliments', value: fb.patients.compliments, color: GOOD })
    second.push({ label: 'Complaints', value: fb.patients.complaints, color: fb.patients.complaints ? BAD : GOOD })
  }
  if (fb.organiser.overall !== null && part('organiserFeedback', 'ratings')) {
    second.push({ label: 'Organiser rating', value: `${fb.organiser.overall} / 5`, sub: `${fb.organiser.responses} response${fb.organiser.responses === 1 ? '' : 's'}`, color: TEAL })
  }

  const findings = keyFindings(data, { include })

  const plannedRows = []
  const presPlan = planned(dp.expectedPresentationsMin, dp.expectedPresentationsMax, dp.expectedPresentationsPlanning)
  if (presPlan) {
    plannedRows.push(['Patient presentations', presPlan, c.total, verdict(c.total, dp.expectedPresentationsMin ?? dp.expectedPresentationsPlanning, dp.expectedPresentationsMax ?? dp.expectedPresentationsPlanning)])
  }
  const hospPlan = planned(dp.hospitalTransfersMin, dp.hospitalTransfersMax, dp.hospitalTransfersPlanning)
  if (hospPlan) {
    plannedRows.push(['Hospital transfers', hospPlan, c.conveyed, verdict(c.conveyed, dp.hospitalTransfersMin ?? dp.hospitalTransfersPlanning, dp.hospitalTransfersMax ?? dp.hospitalTransfersPlanning)])
  }
  if (dp.peakHourStart || dp.peakHourEnd) {
    plannedRows.push([
      'Peak period',
      [dp.peakHourStart, dp.peakHourEnd].filter(Boolean).join(' - '),
      c.peakHour ? `${String(c.peakHour.hour).padStart(2, '0')}:00 - ${String((c.peakHour.hour + 1) % 24).padStart(2, '0')}:00` : '-',
      '',
    ])
  }

  return (
    <ReportPage chrome={chrome}>
      <SectionHeading number={number} title="Executive summary" />
      {part('executive', 'headline') && (
        <>
          <StatRow items={first} />
          <StatRow items={second} />
        </>
      )}

      {part('executive', 'findings') && findings.length ? (
        <>
          <SectionTitle>Key findings</SectionTitle>
          {findings.map((f, i) => (
            <View key={i} style={{ flexDirection: 'row', marginBottom: 3 }} wrap={false}>
              <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: TEAL, marginTop: 3, marginRight: 6 }} />
              <Text style={{ flex: 1, fontSize: 8, color: INK, lineHeight: 1.4 }}>{clean(f)}</Text>
            </View>
          ))}
        </>
      ) : null}

      {part('executive', 'forecast') && plannedRows.length ? (
        <>
          <SectionTitle>Planned against actual</SectionTitle>
          <DataTable
            columns={[
              { label: 'Measure', flex: 2 },
              { label: 'Forecast in the medical plan', flex: 2 },
              { label: 'Actual', flex: 1, align: 'right' },
              { label: '', flex: 1.3, align: 'right' },
            ]}
            rows={plannedRows}
          />
        </>
      ) : null}

      {part('executive', 'debrief') && pea?.summary?.trim() ? (
        <>
          <SectionTitle>Debrief summary</SectionTitle>
          <Prose text={pea.summary} />
        </>
      ) : null}
    </ReportPage>
  )
}

export const EventOverview = ({ number, data, chrome, include }) => {
  const { event } = data
  const s = data.staffing.overall
  const rr = event.resourceRecommendation || {}
  const room = data.controlRoom
  const part = (key) => include.part('overview', key)

  const plannedResource = [
    ['Clinical staff', rr.clinicalStaff],
    ['Paramedics', rr.paramedics],
    ['Nurses', rr.nurses],
    ['Doctors', rr.doctors],
    ['First aid staff', rr.firstAidStaff],
    ['Welfare staff', rr.welfareStaff],
    ['Command staff', rr.commandStaff],
    ['Ambulances', rr.ambulances],
    ['Medical posts', rr.medicalPosts],
    ['Roaming teams', rr.roamingTeams],
  ].filter(([, v]) => v)

  // Only the staffing tiles the planner kept
  const staffTiles = [
    part('staff') && { label: 'Staff deployed', value: s.people },
    part('shiftFill') && { label: 'Shifts filled', value: `${s.filled}/${s.shifts}`, sub: s.fillRate !== null ? `${s.fillRate}% fill rate` : null, color: s.unfilled ? WARN : GOOD },
    part('hours') && { label: 'Planned hours', value: s.plannedHours },
    part('hours') && { label: 'Clocked hours', value: s.clockedShifts ? s.workedHours : '-', sub: s.clockedShifts ? `${s.clockedShifts} shifts clocked` : 'clock not used' },
    part('punctuality') && { label: 'On time', value: fmtPct(s.onTimeRate), color: TEAL },
  ].filter(Boolean)
  const tileRows = []
  for (let i = 0; i < staffTiles.length; i += 2) tileRows.push(staffTiles.slice(i, i + 2))

  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Event and resourcing"
        intro="The event as planned, and the people and vehicles we deployed to it."
      />
      <InPairs
        blocks={[
          part('details') && (
            <>
              <SectionTitle marginTop={0}>Event details</SectionTitle>
              <DataTable
                columns={[{ label: 'Item', flex: 1 }, { label: 'Detail', flex: 2 }]}
                rows={[
                  ['Event', event.title],
                  ['Client', event.client || '-'],
                  ['Venue', event.address || event.location || '-'],
                  ['Starts', fmtDateTime(event.startDate)],
                  ['Ends', fmtDateTime(event.endDate)],
                  ['Event type', event.eventType || '-'],
                  ['Expected attendance', event.expectedAttendance || '-'],
                  ['Clinical risk level', event.clinicalRiskLevel ? event.clinicalRiskLevel.replace('_', ' ') : '-'],
                  ...(room
                    ? [
                        ['Control room open', `${room.open.hours} h over ${room.open.sessions} session${room.open.sessions === 1 ? '' : 's'}`],
                      ]
                    : []),
                ]}
              />
            </>
          ),
          staffTiles.length > 0 && (
            <>
              <SectionTitle marginTop={0}>Staffing delivered</SectionTitle>
              {tileRows.map((row, i) => (
                <StatRow key={i} items={row} />
              ))}
            </>
          ),
        ]}
      />

      <InPairs
        blocks={[
          part('roles') && (
            <>
              <SectionTitle>Staff by role</SectionTitle>
              <CountTable rows={topEntries(s.roles, 12)} total={s.filled} />
            </>
          ),
          part('recommended') && (
            <>
              <SectionTitle>Recommended in the medical plan</SectionTitle>
              <CountTable rows={plannedResource} emptyText="No resource recommendation recorded" />
            </>
          ),
        ]}
      />

      {part('vehicles') && (
        <>
          <SectionTitle>Vehicles deployed</SectionTitle>
          <DataTable
            columns={[
              { label: 'Call sign', flex: 1 },
              { label: 'Registration', flex: 1 },
              { label: 'From', flex: 1.4 },
              { label: 'To', flex: 1.4 },
              { label: 'Hours', flex: 0.7, align: 'right' },
            ]}
            rows={data.staffing.vehicles.map((v) => [
              v.callSign || '-',
              v.regPlate || '-',
              fmtDateTime(v.start),
              fmtDateTime(v.end),
              v.hours ?? '-',
            ])}
            emptyText="No vehicles were booked to this event"
          />
        </>
      )}
      {part('shiftFill') && s.subcontracted ? (
        <Callout title="Subcontracted staff">
          {clean(`${s.subcontracted} of the ${s.filled} filled shifts were covered by subcontracted staff working to our clinical governance.`)}
        </Callout>
      ) : null}
    </ReportPage>
  )
}
